<template>
  <div class="pfw" v-loading="loading">
    <div class="pfw__hd">
      <span class="pfw__title">本项目表单</span>
      <span v-if="xmmc" class="pfw__sub text-muted">{{ xmmc }}</span>
    </div>
    <el-empty v-if="!loading && !formGroups.length" description="当前角色暂无可见表单" :image-size="56" />
    <div v-for="grp in formGroups" :key="grp.phase" class="pfw__group" :ref="'grp-' + grp.phase">
      <div class="pfw__group-hd" @click="toggle(grp.phase)">
        <i class="el-icon-arrow-right pfw__arrow" :class="{ 'is-open': isOpen(grp.phase) }" />
        <span class="pfw__group-title">{{ grp.label }}</span>
        <el-tag size="mini" type="info">{{ grp.forms.length }}</el-tag>
      </div>
      <div v-show="isOpen(grp.phase)" class="pfw__group-body">
          <el-row :gutter="12">
            <el-col :span="12" v-for="form in grp.forms" :key="form.instanceKey || (form.num + '-' + (form.bdslId || ''))">
              <ProjectFormCard :form="form" @open="$emit('open-form', $event)" />
            </el-col>
          </el-row>
        </div>
    </div>
  </div>
</template>

<script>
import api from '@/utils/api'
import ProjectFormCard from '@/components/ivan/ProjectFormCard.vue'

const PHASE_ORDER = [
  { phase: 'dispatch', label: '任务派遣' },
  { phase: 'sampling', label: '现场采样' },
  { phase: 'handover', label: '样品交接' },
  { phase: 'lab', label: '理化分析' },
  { phase: 'other', label: '其他' }
]

/** 工作台 phase.id → 表单分组 key */
function mapActivePhaseToFormGroup(phases) {
  if (!Array.isArray(phases)) return 'sampling'
  const active = phases.find(p => p && p.state === 'active')
  const id = String((active && active.id) || '').toLowerCase()
  if (/dispatch|派遣|135/.test(id)) return 'dispatch'
  if (/sample|field|采样|现场/.test(id)) return 'sampling'
  if (/hand|交接|接样/.test(id)) return 'handover'
  if (/lab|理化|分析/.test(id)) return 'lab'
  // 按 label 再猜
  const lab = String((active && active.label) || '')
  if (/派遣/.test(lab)) return 'dispatch'
  if (/采样|现场/.test(lab)) return 'sampling'
  if (/交接/.test(lab)) return 'handover'
  if (/理化|分析|报告/.test(lab)) return 'lab'
  return 'sampling'
}

export default {
  name: 'ProjectFormWall',
  components: { ProjectFormCard },
  props: {
    xmbh: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      phase: null,
      openMap: {},
      _warmTimer: null
    }
  },
  computed: {
    xmmc() {
      return (this.phase && this.phase.xmmc) || ''
    },
    formGroups() {
      const forms = (this.phase && this.phase.forms) || []
      const map = {}
      forms.forEach(f => {
        const p = f.phase || 'other'
        if (!map[p]) map[p] = []
        map[p].push(f)
      })
      return PHASE_ORDER
        .filter(g => map[g.phase] && map[g.phase].length)
        .map(g => ({ ...g, forms: map[g.phase] }))
    },
    preferPhase() {
      return mapActivePhaseToFormGroup(this.phase && this.phase.phases)
    }
  },
  watch: {
    xmbh: {
      immediate: true,
      handler(v) {
        if (v) this.load()
        else {
          this.phase = null
          this.openMap = {}
        }
      }
    }
  },
  beforeDestroy() {
    if (this._warmTimer) clearTimeout(this._warmTimer)
  },
  methods: {
    isOpen(phase) {
      return !!this.openMap[phase]
    },
    toggle(phase) {
      this.$set(this.openMap, phase, !this.openMap[phase])
    },
    expandPhase(phaseKey) {
      const key = phaseKey || this.preferPhase
      this.formGroups.forEach(g => {
        this.$set(this.openMap, g.phase, g.phase === key)
      })
      this.$nextTick(() => {
        const ref = this.$refs['grp-' + key]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      })
    },
    async load() {
      if (!this.xmbh) return
      this.loading = true
      try {
        const res = await api.get('yn_project_workspace', { xmbh: this.xmbh })
        this.phase = res.data
        if (this.phase && this.phase.error) {
          this.$message.error(this.phase.error)
        }
        const prefer = this.preferPhase
        const next = {}
        this.formGroups.forEach(g => {
          next[g.phase] = g.phase === prefer
        })
        // 多份 135：默认展开「任务派遣」，避免只看到现场采样
        const dispatchGrp = this.formGroups.find(g => g.phase === 'dispatch')
        if (dispatchGrp && dispatchGrp.forms.length > 1) {
          next.dispatch = true
          // 若当前阶段不是派遣，收起其它组，先露出多份派遣单
          if (prefer !== 'dispatch') {
            this.formGroups.forEach(g => {
              if (g.phase !== 'dispatch') next[g.phase] = false
            })
          }
        }
        // 若偏好分组无表，展开第一组
        if (!next[prefer] && !next.dispatch && this.formGroups.length) {
          next[this.formGroups[0].phase] = true
        }
        this.openMap = next
        this.$emit('loaded', this.phase)
        this.scheduleWarm()
      } catch (e) {
        this.$message.error('加载项目表单失败')
      } finally {
        this.loading = false
      }
    },
    scheduleWarm() {
      if (!this.xmbh) return
      if (this._warmTimer) clearTimeout(this._warmTimer)
      this._warmTimer = setTimeout(() => this.warmPreviews(), 600)
    },
    async warmPreviews() {
      if (!this.xmbh || document.visibilityState === 'hidden') return
      let rounds = 0
      let partial = 1
      while (partial && rounds < 4) {
        rounds++
        try {
          const res = await api.get('yn_tr_lab_docx_warm', { xmbh: this.xmbh, budget_ms: 10000 })
          partial = res.data && res.data.partial
        } catch (e) {
          break
        }
        if (document.visibilityState === 'hidden') break
      }
    }
  }
}
</script>

<style scoped>
.pfw {
  margin: 10px;
  background: #fff;
  border-radius: 0.25em;
  box-shadow: rgb(215, 228, 237) 0 3px 0;
  padding: 12px 14px 8px;
  min-height: 120px;
}
.pfw__hd {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 10px;
  padding-left: 8px;
  border-left: 3px solid #1aaeda;
}
.pfw__title {
  font-size: 15px;
  font-weight: 700;
  color: #303133;
}
.pfw__sub {
  font-size: 12px;
}
.pfw__group {
  margin-bottom: 6px;
}
.pfw__group-hd {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 4px;
  user-select: none;
}
.pfw__group-hd:hover {
  background: #f5f7fa;
}
.pfw__arrow {
  transition: transform 0.2s;
  color: #909399;
}
.pfw__arrow.is-open {
  transform: rotate(90deg);
}
.pfw__group-title {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  flex: 1;
}
.pfw__group-body {
  padding: 4px 0 8px;
}
</style>
